<template>
  <div v-if="form" id="edit-profile-modal" uk-modal>
    <div class="uk-modal-dialog uk-modal-body edit-profile-modal">
      <button class="uk-modal-close-default" type="button" uk-close @click="cancelEdit"></button>

      <div class="edit-profile-header">
        <h2 class="uk-modal-title">Edit Details</h2>
        <p>{{ isPetKit ? 'Update the owner and pet profile for this kit.' : 'Update the personal profile for this kit.' }}</p>
      </div>

      <div v-if="errorMessage" class="edit-profile-alert error">{{ errorMessage }}</div>

      <form @submit.prevent="saveChanges" novalidate>
        <div class="edit-profile-section">
          <div class="edit-profile-section-title">{{ isPetKit ? 'Owner Details' : 'Personal Details' }}</div>

          <div class="uk-grid-small" uk-grid>
            <div class="uk-width-1-2@s">
              <label class="field-label" for="edit-first-name">First Name <span class="field-meta">Required</span></label>
              <input id="edit-first-name" v-model.trim="form.firstName" class="uk-input" :class="fieldClass('firstName')" type="text" @blur="markTouched('firstName')">
              <div v-if="showError('firstName')" class="error">{{ fieldError('firstName') }}</div>
            </div>

            <div class="uk-width-1-2@s">
              <label class="field-label" for="edit-last-name">Last Name <span class="field-meta">Required</span></label>
              <input id="edit-last-name" v-model.trim="form.lastName" class="uk-input" :class="fieldClass('lastName')" type="text" @blur="markTouched('lastName')">
              <div v-if="showError('lastName')" class="error">{{ fieldError('lastName') }}</div>
            </div>
          </div>
        </div>

        <div v-if="isPetKit" class="edit-profile-section">
          <div class="edit-profile-section-title">Pet Profile</div>

          <div class="uk-grid-small" uk-grid>
            <div class="uk-width-1-2@s">
              <label class="field-label" for="edit-pet-name">Pet Name <span class="field-meta">Required</span></label>
              <input id="edit-pet-name" v-model.trim="form.petName" class="uk-input" :class="fieldClass('petName')" type="text" @blur="markTouched('petName')">
              <div v-if="showError('petName')" class="error">{{ fieldError('petName') }}</div>
            </div>

            <div class="uk-width-1-2@s">
              <div class="choice-panel" :class="{ error: showError('petType') }">
                <div class="choice-panel-header">
                  <strong>Pet Type</strong>
                  <span class="field-meta">Required</span>
                </div>
                <label v-for="option in petTypeOptions" :key="option.value" class="choice-row">
                  <input class="uk-radio" type="radio" :value="option.value" v-model="form.petType" @change="markTouched('petType')">
                  <span>{{ option.label }}</span>
                </label>
              </div>
              <div v-if="showError('petType')" class="error">{{ fieldError('petType') }}</div>
            </div>
          </div>

          <div class="uk-grid-small" uk-grid>
            <div class="uk-width-1-2@s">
              <label class="field-label" for="edit-pet-breed">Breed <span class="field-meta">Optional</span></label>
              <input id="edit-pet-breed" v-model.trim="form.petBreed" class="uk-input" type="text" :list="petBreedListId">
              <datalist :id="petBreedListId">
                <option v-for="breed in filteredPetBreedOptions" :key="breed" :value="breed">{{ breed }}</option>
              </datalist>
            </div>

            <div class="uk-width-1-2@s">
              <label class="field-label" for="edit-pet-approximate-age">Approximate Age <span class="field-meta">Optional</span></label>
              <input id="edit-pet-approximate-age" v-model.trim="form.petApproximateAge" class="uk-input" type="text" placeholder="Example: 2 years">
            </div>
          </div>
        </div>

        <div v-else class="edit-profile-section">
          <div class="edit-profile-section-title">Profile Details</div>

          <div class="uk-grid-small" uk-grid>
            <div class="uk-width-1-2@s">
              <label class="field-label" for="edit-gender">Gender <span class="field-meta">Optional</span></label>
              <select id="edit-gender" v-model="form.gender" class="uk-select" @blur="markTouched('gender')">
                <option value="">Select gender</option>
                <option v-for="option in humanGenderOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
            </div>

            <div v-if="form.gender === 'self_describe'" class="uk-width-1-2@s">
              <label class="field-label" for="edit-gender-self">Describe Gender <span class="field-meta">Required</span></label>
              <input id="edit-gender-self" v-model.trim="form.genderSelfDescribe" class="uk-input" :class="fieldClass('genderSelfDescribe')" type="text" @blur="markTouched('genderSelfDescribe')">
              <div v-if="showError('genderSelfDescribe')" class="error">{{ fieldError('genderSelfDescribe') }}</div>
            </div>
          </div>

          <div class="uk-grid-small" uk-grid>
            <div class="uk-width-1-2@s">
              <label class="field-label" for="edit-birth-month">Birth Month <span class="field-meta">Optional</span></label>
              <select id="edit-birth-month" v-model="form.birthMonth" class="uk-select" @blur="markTouched('birthMonth')">
                <option value="">Select month</option>
                <option v-for="month in months" :key="month.value" :value="month.value">{{ month.label }}</option>
              </select>
              <div v-if="showError('birthMonth')" class="error">{{ fieldError('birthMonth') }}</div>
            </div>

            <div class="uk-width-1-2@s">
              <label class="field-label" for="edit-birth-year">Birth Year <span class="field-meta">Optional</span></label>
              <input id="edit-birth-year" v-model="form.birthYear" class="uk-input" :class="fieldClass('birthYear')" type="text" inputmode="numeric" maxlength="4" placeholder="YYYY" @input="handleBirthYearInput" @blur="markTouched('birthYear')">
              <div v-if="showError('birthYear')" class="error">{{ fieldError('birthYear') }}</div>
            </div>
          </div>
        </div>

        <div class="edit-profile-actions">
          <button class="uk-button uk-button-primary" type="submit" :disabled="submitting">
            <span v-if="submitting" uk-spinner="ratio: 0.6" class="uk-margin-small-right"></span>
            {{ submitting ? 'Saving...' : 'Save Changes' }}
          </button>
          <button class="uk-button uk-button-default" type="button" @click="cancelEdit">Cancel</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {
  catBreedOptions,
  dogBreedOptions,
  humanGenderOptions,
  petTypeOptions
} from '@/utils/registrationForms';

const MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
];

function normalizeHumanGender(value) {
  const normalized = String(value || '').trim().toLowerCase().replace(/[-\s]+/g, '_');
  if (['female', 'male', 'non_binary', 'self_describe', 'prefer_not_to_say'].includes(normalized)) {
    return normalized;
  }
  if (normalized === 'other') return 'self_describe';
  return '';
}

function legacyGenderLabel(value, selfDescribe) {
  switch (value) {
    case 'female':
      return 'Female';
    case 'male':
      return 'Male';
    case 'non_binary':
      return 'Non-binary';
    case 'self_describe':
      return selfDescribe || 'Self-describe';
    case 'prefer_not_to_say':
      return 'Prefer not to say';
    default:
      return null;
  }
}

function petTypeLabel(value) {
  if (value === 'cat') return 'Cat';
  if (value === 'dog') return 'Dog';
  return null;
}

export default {
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: null,
      touched: {},
      formSubmitted: false,
      submitting: false,
      errorMessage: '',
      accessToken: localStorage.getItem('accessToken'),
      cid: localStorage.getItem('cid'),
      humanGenderOptions,
      petTypeOptions,
      dogBreedOptions,
      catBreedOptions,
      months: MONTHS,
    };
  },
  computed: {
    isPetKit() {
      return this.form?.subjectType === 'pet';
    },
    filteredPetBreedOptions() {
      return this.form?.petType === 'cat' ? this.catBreedOptions : this.dogBreedOptions;
    },
    petBreedListId() {
      return `edit-pet-breed-options-${this.form?.petType || 'dog'}`;
    },
    validationErrors() {
      if (!this.form) return {};

      const errors = {};
      if (!String(this.form.firstName || '').trim()) errors.firstName = 'First name is required.';
      if (!String(this.form.lastName || '').trim()) errors.lastName = 'Last name is required.';

      if (this.isPetKit) {
        if (!String(this.form.petName || '').trim()) errors.petName = 'Pet name is required.';
        if (!this.form.petType) errors.petType = 'Choose dog or cat.';
      } else {
        if (this.form.gender === 'self_describe' && !String(this.form.genderSelfDescribe || '').trim()) {
          errors.genderSelfDescribe = 'Please describe your gender.';
        }

        const hasBirthMonth = !!this.form.birthMonth;
        const hasBirthYear = !!this.form.birthYear;
        if (hasBirthMonth !== hasBirthYear) {
          if (!hasBirthMonth) errors.birthMonth = 'Select a birth month.';
          if (!hasBirthYear) errors.birthYear = 'Enter a valid birth year.';
        }

        if (hasBirthMonth) {
          const month = Number(this.form.birthMonth);
          if (!Number.isInteger(month) || month < 1 || month > 12) errors.birthMonth = 'Select a birth month.';
        }

        if (hasBirthYear) {
          const year = Number(this.form.birthYear);
          const currentYear = new Date().getFullYear();
          if (!Number.isInteger(year) || year < 1900 || year > currentYear) errors.birthYear = 'Enter a valid birth year.';
        }
      }

      return errors;
    },
  },
  watch: {
    item: {
      immediate: true,
      handler(newItem) {
        if (!newItem) return;

        const isPetKit = this.isPetItem(newItem);
        const gender = normalizeHumanGender(newItem.HumanGender || newItem.Gender);

        this.form = {
          kitId: newItem.KitId,
          subjectType: isPetKit ? 'pet' : 'human',
          firstName: newItem.FirstName || '',
          lastName: newItem.LastName || '',
          petName: newItem.PetName || '',
          petType: String(newItem.PetType || '').trim().toLowerCase() === 'cat' ? 'cat' : 'dog',
          petBreed: newItem.PetBreed || '',
          petApproximateAge: newItem.PetApproximateAge || '',
          gender,
          genderSelfDescribe: gender === 'self_describe'
            ? (newItem.HumanGenderSelfDescribe || (newItem.Gender && !['Male', 'Female', 'Non-binary', 'Prefer not to say'].includes(newItem.Gender) ? newItem.Gender : ''))
            : '',
          birthMonth: newItem.HumanBirthMonth ? String(newItem.HumanBirthMonth) : '',
          birthYear: newItem.HumanBirthYear ? String(newItem.HumanBirthYear) : '',
        };
        this.touched = {};
        this.formSubmitted = false;
        this.errorMessage = '';
      },
    },
  },
  methods: {
    isPetItem(item) {
      const subjectType = String(item?.RegistrationSubjectType || '').toLowerCase();
      if (subjectType) return subjectType === 'pet';

      const typeDescription = String(item?.TypeDescription || '').toLowerCase();
      const testKitType = String(item?.TestKitType || '').toLowerCase();
      const typeId = Number(item?.TestTypeId || item?.TestKitTypeId);
      return typeDescription === 'pets' || testKitType === 'pet' || [4, 9, 11].includes(typeId) || !!item?.PetName;
    },
    markTouched(field) {
      this.touched = { ...this.touched, [field]: true };
    },
    fieldError(field) {
      return this.validationErrors[field] || '';
    },
    showError(field) {
      return (this.touched[field] || this.formSubmitted) && !!this.fieldError(field);
    },
    fieldClass(field) {
      return { 'error-border': this.showError(field) };
    },
    handleBirthYearInput(event) {
      this.form.birthYear = event.target.value.replace(/\D/g, '').slice(0, 4);
    },
    cancelEdit() {
      this.$emit('close');
    },
    async saveChanges() {
      this.formSubmitted = true;
      this.errorMessage = '';

      if (Object.keys(this.validationErrors).length > 0) {
        this.errorMessage = 'Please correct the highlighted fields.';
        return;
      }

      const subjectType = this.form.subjectType;
      const humanGenderSelfDescribe = subjectType === 'human' && this.form.gender === 'self_describe'
        ? String(this.form.genderSelfDescribe || '').trim() || null
        : null;

      const payload = {
        kitId: this.form.kitId,
        subjectType,
        newFirstName: String(this.form.firstName || '').trim(),
        newLastName: String(this.form.lastName || '').trim(),
        newPetName: subjectType === 'pet' ? String(this.form.petName || '').trim() : null,
        newPetType: subjectType === 'pet' ? petTypeLabel(this.form.petType) : null,
        newPetBreed: subjectType === 'pet' ? String(this.form.petBreed || '').trim() || null : null,
        newPetApproximateAge: subjectType === 'pet' ? String(this.form.petApproximateAge || '').trim() || null : null,
        newGender: subjectType === 'human' ? legacyGenderLabel(this.form.gender, humanGenderSelfDescribe) : null,
        newHumanGender: subjectType === 'human' ? (this.form.gender || null) : null,
        newHumanGenderSelfDescribe: humanGenderSelfDescribe,
        newHumanBirthMonth: subjectType === 'human' && this.form.birthMonth ? Number(this.form.birthMonth) : null,
        newHumanBirthYear: subjectType === 'human' && this.form.birthYear ? Number(this.form.birthYear) : null,
      };

      this.submitting = true;

      try {
        await axios.post(`${process.env.VUE_APP_API_URL}/api/update-profile`, payload, {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'cid': this.cid,
          },
        });

        this.$emit('item-updated', {
          ...this.item,
          FirstName: payload.newFirstName,
          LastName: payload.newLastName,
          PetName: payload.newPetName,
          PetType: payload.newPetType,
          PetBreed: payload.newPetBreed,
          PetApproximateAge: payload.newPetApproximateAge,
          Gender: payload.newGender,
          HumanGender: payload.newHumanGender,
          HumanGenderSelfDescribe: payload.newHumanGenderSelfDescribe,
          HumanBirthMonth: payload.newHumanBirthMonth,
          HumanBirthYear: payload.newHumanBirthYear,
          RegistrationSubjectType: subjectType,
        });
      } catch (error) {
        this.errorMessage = 'Failed to update registration: ' + (error.response?.data?.error || error.message);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.edit-profile-modal {
  width: min(720px, calc(100vw - 32px));
  padding: 28px;
  border-radius: 20px;
  background: #ffffff;
}

.edit-profile-header p {
  margin: 8px 0 0;
  color: #536051;
}

.edit-profile-eyebrow {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #71806e;
}

form {
  border: none;
  padding: 0;
}
.edit-profile-section-title {
  margin-top: 20px;
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.field-label {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  font-weight: 600;
}

.field-meta {
  font-size: 12px;
  font-weight: 500;
  color: #71806e;
}

.choice-panel-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.choice-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-right: 16px;
}

.edit-profile-actions {
  display: flex;
  gap: 12px;
  margin-top: 22px;
}

.edit-profile-alert {
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 12px;
}

.edit-profile-alert.error {
  background: #fff4e8;
  color: #8d4a09;
}

.error {
  margin-top: 6px;
  color: #c43d30;
  font-size: 13px;
}

.error-border {
  border-color: #c43d30;
}

@media (max-width: 639px) {
  .edit-profile-modal {
    padding: 22px 18px;
    border-radius: 16px;
  }

  .field-label,
  .choice-panel-header,
  .edit-profile-actions {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
